<template>
  <v-layout v-if="isStoreLoading" justify-center style="padding: 40vh 0px;">
    <v-progress-circular
      :size="70"
      :width="7"
      color="primary"
      indeterminate
      class="mt-3 mb-3"
    ></v-progress-circular>
  </v-layout>

  <div v-else id="StoreSubHeader" :key="getStore ? getStore.storeId : 'loading'">
    <notice-alert :notice-text="noticeText" />
    <img :src="getImage || '/owa/img/2db4f3c6a8e5c2eb7f6450ddcf56f585a36a972d.png'"
      :class="{ 'smallBannerImage': $vuetify.breakpoint.smAndDown || !(getLogoImage && !hasMainImageError) }"
      id="bannerImage"
      :style="{ maxHeight: '178px', marginTop: bannerMarginTop + 'px' }"
      v-if="!shouldUseCustomHeader"
    />

    <div id="storeDetails" :style="`background-color: ${$vuetify.theme.menu_colour};`">
      <div>
        <div id="storeLocation">
          <StoreLocationDetails :store="getStore" />
        </div>
        <template v-if="showFullSubHeader && (!$store.getters['groupOrder/isInGroupOrder'] || $store.state.groupOrder.isHost) && !$store.getters['sharedCart/isActive']" style="margin-left: 24px; margin-right: 24px;">
          <promotion-rewards-container :key="getStore ? getStore.storeId + '_promos' : 'no-store-promos'"/>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
  import get from 'lodash/get';
  import isEmpty from 'lodash/isEmpty';
  import { mapGetters, mapMutations, mapActions } from 'vuex'
  import { EventBus } from "@/services/event-bus"
  import { parseDeliveryAddress, checkLocation, isNullOrEmpty } from "@/services/util.service"

  import StoreLocationDetails from "@/components/StoreLocationDetails"
  import NoticeAlert from "./NoticeAlert"
  import ExternalAppsLinks from "@/components/ExternalAppsLinks"
  import RewardsCarousel from "@/components/RewardsOptions/RewardsCarousel"
  import CouponCardContainer from "@/components/Coupons/CouponCardContainer"
  import PromotionRewardsContainer from "@/components/PromotionRewards/PromotionRewardsContainer"
  import OrderTypeSelect from '@/components/OrderTypeSelect.vue'
  import deliveryProvider from '@/components/DeliveryProvider.vue'


  export default {
    components: {
      NoticeAlert,
      StoreLocationDetails,
      ExternalAppsLinks,
      RewardsCarousel,
      CouponCardContainer,
      OrderTypeSelect,
      deliveryProvider,
      PromotionRewardsContainer
    },

    data: () => ({
      hasMainImageError: false,

      deliveryAddress: '',
      hasAddressError: false,
      countryCode: null,
      dineInOnlyQueryParam: false,
      bannerMarginTop: 0
    }),
    created() {
      this.dineInOnlyQueryParam = this.$route.query.dineInOnly;
    },
    computed: {
      isMobile() {
        const userAgent = window.navigator.userAgent.toLowerCase();
        return this.$vuetify.breakpoint.smAndDown || /mobile|android|iphone|ipad|ipod/.test(userAgent)
      },
      showFullSubHeader() {
        return this.$route.name == 'store'
      },
      noticeText() {
        const customNotice = get(this.getStore, 'settings.owaSettings.noticeMessage.translations', []);
        let matchedCustomNotice = '';

        if (this.$vuetify.lang) {
          const matched = customNotice.find(t => t.lang === this.$vuetify.lang.current);
          matchedCustomNotice = matched ? matched.value : '';
        }

        if (matchedCustomNotice && matchedCustomNotice.trim() !== '') {
          return matchedCustomNotice;
        }

        if (this.showFullSubHeader) {
          return this.getStoreNotice;
        }

        return '';
      },

      getLogoImage() {
        const path = process.env.VUE_APP_API_IMAGE
        const relativePath = get(this.getStore, 'images.OWA_LOGO[0].url') || get(this.getStore, 'images.LOGO[0].url')

        if (relativePath) {
          return `${path}${relativePath}`
        } else {
          this.hasMainImageError = true
        }
      },

      getImage() {
        const path = process.env.VUE_APP_API_IMAGE
        const mobilePath = get(this.getStore, 'images.MAIN[0].url')
        const desktopPath = get(this.getStore, 'images.DESKTOP_MAIN[0].url')
        const relativePath = this.$vuetify.breakpoint.smAndDown ? mobilePath : desktopPath || mobilePath

        if (relativePath)
          return `${path}${relativePath}`
      },

      inMenuOffHours() {
        const now = new Date()
        const { start } = this.menuAvailableTime()

        return start !== null && now.getHours() < start && this.isMenuAvailable()
      },

      allowDeliveryProviders() {
        // Don't allow selection if delivery is handled by store
        const currentDeliveryProvider = get(this.getStore, 'settings.deliverySettings.currentDeliveryProvider')

        return currentDeliveryProvider != 'SELF'
      },
      storeDeliveryProviders() {
        if (this.allowDeliveryProviders)
          return get(this.getStore, 'settings.deliverySettings.supportedDeliveryProviders', []) || []
        return []
      },
      registerAvailable() {
        return get(this.getStore, 'store.settings.owaSettings.registerMembership', false)
      },
      showServiceButton() {
        return this.isDineIn && !this.inFastServiceDineIn && get(this.getStore, 'features.serviceRequestSupported', false)
      },

      ...mapGetters([
        'shouldUseCustomHeader',
        'getGroup',
        'getStore',
        'getTableId',
        'testStore',
        'isStoreLoading',
        'getStoreNotice',
        'storeIsOpen',
        'storeIsAvailable',
        'getStoreAvailableOrderTimes',
        'isMenuAvailable',
        'getOrderType',
        'isPickUp',
        'isDelivery',
        'isDineIn',
        'inFastServiceDineIn',
        'menuAvailableTime',
        'getStoreCountryCode',
        'getDeliveryProvider',
        'cartErrorMessage',
        'getDeliveryAddress'
      ])
    },

    methods: {
      disableAutocomplete() {
        setTimeout(() => {
          const ref = document.getElementById('map')

          if (ref)
            ref.autocomplete = 'none'
        }, 1000)
      },

      truncate(str, n){
        return (str.length > n) ? str.slice(0, n-1) + '...' : str;
      },

      onAddressError(data) {
        this.$vuetify.goTo('#delivery-address')
        this.$nextTick(() => this.$refs.googleAddress.clear())
        this.hasAddressError = true
      },
      clearDeliveryAddress() {
          this.setDeliveryAddress(null)
          this.deliveryAddress = null
      },
      registerPage() {
      const { gid, sid, mid } = this.$route.params;
        this.$router.push({
          name: 'register',
          params: {
            gid,
            sid,
            mid
          }
        }).catch(() => {});
      },
      parseAddressData(addressData, placeResultData, id) {
        this.hasAddressError = false
        const errorDeliveryAddress = (errString) => {
          this.showSnack({
            text: errString ? this.$vuetify.t(errString) : 'error',
            color: 'error'
          })

          this.clearDeliveryAddress()
        }

        if (!isEmpty(addressData)) {
          const payload = parseDeliveryAddress(null, addressData, placeResultData)
          const hasCoordinates = !!(payload.gpsCoordinate && payload.gpsCoordinate.latitude && payload.gpsCoordinate.longitude)

          if (!payload.postCode && !hasCoordinates) {
            errorDeliveryAddress('$vuetify.order.deliveryAddressInvalid')
            return
          }

          this.$store.dispatch('validateDeliveryAddress', payload).then((res) => {
            if (!!res.data.error) {
              errorDeliveryAddress(get(res, 'data.error.errorMsg', get(res, 'data.error')))

              return
            }

            this.setDeliveryAddress({
              address: payload,
              result: placeResultData
            })

            this.SET_CHECKOUT_INFO({
              ...this.getCheckoutInfo,
              unitNumber: null
            })

            this.findMostValidProvider(get(res, 'data.result', []))
          }).catch(error => {
            console.error('Address Validation failed', error);
            errorDeliveryAddress('$vuetify.order.deliveryAddressMustBeInRange')
          })
        } else if (this.getDeliveryAddress) {
          this.clearDeliveryAddress()
        }
      },

      findMostValidProvider(providers) {
        // Stores can support many different delivery providers
        // Since it is expensive to find the most correct one for the customer,
        // validate is the only one that checks and we save the result to provide later
        if (!isNullOrEmpty(providers)) {
          this.SET_DELIVERY_PROVIDERS_DETAILS(providers)
          const mostValid = providers.find(_provider => _provider.valid)

          if (!!mostValid) {
            this.setDeliveryProvider(mostValid.provider)
          } else {
            this.showSnack({
              text: 'No delivery providers support that address',
              color: 'error'
            })

            this.setDeliveryAddress(null)
            this.setDeliveryProvider(null)
            this.deliveryAddress = null

            if (this.$refs.googleAddress)
              this.$refs.googleAddress.clear()
          }
        }
      },
      setDeliveryProvider(newProvider) {
        let obj = this.getDeliveryProvider || {}
        obj[this.getStore.storeId] = newProvider

        this.SET_DELIVERY_PROVIDER(obj)
      },

      async attemptToOpenServer() {
        const { verifyLocation } = this.getStore.settings.owaSettings
        const tableId = this.getTableId
        // Only check location if the merchant has stated they care
        if (verifyLocation)
          await checkLocation()

        if (verifyLocation && !this.isInStore) {
          // Location check was run and they are out of range... block other actions
        } else if (!this.isDineInTableSet && tableId < 0) {
          this.showSnack({
            text: this.$vuetify.t('$vuetify.requestService.tableNeeded'),
            color: 'warning'
          })

          // Allow user to provide table number before making requests
          this.waitForTable = true
          this.setQrScanner(true)
        } else {
          this.SET_CALL_SERVER_DIALOG(true)
        }
      },
      updateBannerMargin() {
        const notice = document.getElementById('storeNoticeAlert');
        if (!notice) {
          this.bannerMarginTop = 0;
          return;
        }
        const parent = notice.parentElement || notice;
        const computed = window.getComputedStyle(parent);
        const isFixed = computed.position === 'fixed' || parent.classList.contains('fixed-notice') || notice.classList.contains('fixed-notice');
        this.bannerMarginTop = isFixed ? parent.offsetHeight : 0;
      },

      ...mapMutations([ 'SET_CALL_SERVER_DIALOG', 'SET_CHECKOUT_INFO', 'SET_DELIVERY_PROVIDER', 'SET_DELIVERY_PROVIDERS_DETAILS' ]),
      ...mapActions([ 'showSnack', 'setQrScanner', 'setDeliveryAddress' ])
    },

    watch: {
      getOrderType() {
        this.disableAutocomplete()
      }
    },

    mounted() {
      const result = this.getDeliveryAddressResult
      this.deliveryAddress = get(result, 'formatted_address', '')

      EventBus.$on('delivery-address-error', this.onAddressError);


      if (!!this.getDeliveryAddress) {
        if (this.getStoreDeliveryProvider && !this.allowDeliveryProviders) {
          // If selecting a provider is not allowed, clear the selection for this store
          this.setDeliveryProvider(null)
        } else if (!this.getStoreDeliveryProvider) {
          // Store delivery provider is new. The customer might have their address set but no provider
          this.$store.dispatch('validateDeliveryAddress', this.getDeliveryAddress).then(res => {
            this.findMostValidProvider(get(res, 'data.result', []))
          })
        } else {
          // We also want to update the provider info incase their fees changed
          this.$store.dispatch('validateDeliveryAddress', this.getDeliveryAddress).then(res => {
            this.SET_DELIVERY_PROVIDERS_DETAILS(get(res, 'data.result', []))
          })
        }
        this.deliveryAddress = this.getDeliveryAddress
      }

      setTimeout(() => {
        this.disableAutocomplete()
      }, 1000)

      // Setup dynamic banner offset relative to notice alert if position fixed
      this.$nextTick(() => {
        this.updateBannerMargin();
        const notice = document.getElementById('storeNoticeAlert');
        if (notice && 'ResizeObserver' in window) {
          this._noticeObserver = new ResizeObserver(() => this.updateBannerMargin());
          this._noticeObserver.observe(notice);
        }
        window.addEventListener('scroll', this.updateBannerMargin, { passive: true });
        window.addEventListener('resize', this.updateBannerMargin, { passive: true });
      });
    },
    beforeDestroy() {
      if (this._noticeObserver) {
        this._noticeObserver.disconnect();
        this._noticeObserver = null;
      }
      window.removeEventListener('scroll', this.updateBannerMargin);
      window.removeEventListener('resize', this.updateBannerMargin);
    }
  }
</script>

<style>
  #storeLocation #storeName {
    font-size: var(--text-size-3);

    margin: 20px auto 15px;
  }
  #deliveryProviderContainer .hover-animation-container {
    padding-left: 20px;
    padding-right: 20px;
  }
</style>
<style scoped>
  #bannerImage {
    z-index: 0;

    height: 25vh;
    max-height: 178px !important;
    width: 100vw;

    object-fit: cover;
  }
  .smallBannerImage {
    height: 12.5vh !important;
  }
  #mainLogoImage {
    margin: auto;

    z-index: 1;

    height: 25vh;
    max-width: 40vh;
  }

  #storeDetails {
    position: relative;

    width: 100vw;
    margin-top: -6px !important;
  }
  #storeDetails #mainLogoImageContainer {
    position: absolute;
    top: -20vh;
    
    width: 100vw;
    height: 25vh;
  }

  #autocompleteContainer {
    max-width: 520px !important;
    padding: 0px 10px;
    margin: auto;
  }

  .extraAvailableDetails {
    font-size: 15px;
    margin: 12px 0px;
  }
</style>
